"use client";

import { type ComponentProps, type CSSProperties, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "~/components/ui/button";
import { marketingAppDashboardUrl } from "~/lib/auth-paths";
import { MARKETING_BUILD_CUSTOM_NEW } from "~/lib/inbound-gpt/paths";
import { SITEGLUE_CTA_CONFIG, trackSiteGlueCta } from "~/lib/siteglue/cta-config";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { cn } from "~/lib/utils";

type BuildAgentForFreeButtonProps = {
  className?: string;
  style?: CSSProperties;
  size?: ComponentProps<typeof Button>["size"];
  variant?: ComponentProps<typeof Button>["variant"];
  testId?: string;
  fullWidth?: boolean;
  showArrow?: boolean;
  children?: ReactNode;
  /** Runs before navigation (e.g. persist pricing calculator selection). */
  onBeforeNavigate?: () => void;
  /** Analytics location when SiteGlue brand. */
  ctaLocation?:
    | "header"
    | "mobile_menu"
    | "hero"
    | "pricing_card"
    | "pricing_hero"
    | "pricing_final"
    | "feature_section"
    | "demo_card"
    | "footer"
    | "final_cta"
    | "industry_hero"
    | "industry_final"
    | "empty_state";
};

/** Opens a fresh hire/onboarding session in a new tab (clears stale cache via ?new=1). */
export function BuildAgentForFreeButton({
  className,
  style,
  size = "lg",
  variant = "default",
  testId = "button-build-agent-free",
  fullWidth = false,
  showArrow = false,
  children,
  onBeforeNavigate,
  ctaLocation = "final_cta",
}: BuildAgentForFreeButtonProps) {
  const isSiteGlue = getMarketingChrome().brandId === "siteglue";
  const label =
    children ??
    (isSiteGlue ? SITEGLUE_CTA_CONFIG.signup.label : "Try It Free");
  // Marketing hosts (incl. SiteGlue) resolve to app.easygpt.builders with brand= when needed.
  const href = marketingAppDashboardUrl(MARKETING_BUILD_CUSTOM_NEW);

  return (
    <Button
      asChild
      size={size}
      variant={variant}
      style={style}
      className={cn(fullWidth && "w-full", className)}
      data-testid={testId}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          if (isSiteGlue) {
            trackSiteGlueCta({
              action: "signup",
              label: typeof label === "string" ? label : SITEGLUE_CTA_CONFIG.signup.label,
              location: ctaLocation,
              destination: href,
            });
          }
          onBeforeNavigate?.();
        }}
      >
        {label}
        {showArrow ? <ArrowRight className="ml-1 h-4 w-4" /> : null}
      </a>
    </Button>
  );
}
